import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Brain,
  RefreshCw,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Zap,
  ArrowLeft
} from 'lucide-react';

const AutonomyAgents = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [agentMeta, setAgentMeta] = useState({ roles: [], autonomy_levels: [] });

  const fetchAgentMeta = useCallback(async () => {
    try {
      const res = await api.get('/admin/autonomy/agents/roles');
      setAgentMeta({
        roles: res.data.roles || [],
        autonomy_levels: res.data.autonomy_levels || []
      });
    } catch (error) {
      console.error('Failed to fetch agent metadata:', error);
    }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/autonomy/agents');
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgentMeta();
    fetchAgents();
  }, [fetchAgentMeta, fetchAgents]);

  const autonomyLevelConfig = {
    'full_auto': { color: 'bg-green-100 text-green-800', label: 'Full Auto' },
    'supervised': { color: 'bg-blue-100 text-blue-800', label: 'Supervised' },
    'approval_required': { color: 'bg-yellow-100 text-yellow-800', label: 'Approval Required' },
    'manual_only': { color: 'bg-gray-100 text-gray-800', label: 'Manual Only' },
    'disabled': { color: 'bg-red-100 text-red-800', label: 'Disabled' }
  };

  const roleColorMap = {
    'arrears_agent': 'text-orange-600 bg-orange-50',
    'maintenance_agent': 'text-blue-600 bg-blue-50',
    'compliance_agent': 'text-purple-600 bg-purple-50',
    'onboarding_agent': 'text-green-600 bg-green-50',
    'communication_agent': 'text-cyan-600 bg-cyan-50',
    'scheduling_agent': 'text-indigo-600 bg-indigo-50',
    'document_agent': 'text-pink-600 bg-pink-50',
    'analytics_agent': 'text-yellow-600 bg-yellow-50'
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-agents-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">AI Agents</h1>
                <p className="text-sm text-gray-500">Autonomous agents handling system tasks</p>
              </div>
            </div>
            <Button onClick={fetchAgents} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Agents</p>
                  <p className="text-2xl font-bold">{agents.length}</p>
                </div>
                <Brain className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">
                    {agents.filter(a => a.is_active).length}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Tasks Completed</p>
                  <p className="text-2xl font-bold">
                    {agents.reduce((sum, a) => sum + (a.tasks_completed || 0), 0)}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Escalations</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {agents.reduce((sum, a) => sum + (a.escalations || 0), 0)}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agents Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : agents.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No agents configured</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <Card key={agent.id} className="hover:shadow-md transition-shadow" data-testid={`agent-${agent.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${roleColorMap[agent.agent_role] || 'bg-gray-100'}`}>
                        <Brain className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base capitalize">
                          {(agent.agent_role || 'unknown').replace('_', ' ')}
                        </CardTitle>
                        {agent.agent_instance && (
                          <p className="text-xs text-gray-400">{agent.agent_instance}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={agent.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Autonomy Level */}
                  <div className="mb-4">
                    <Badge className={autonomyLevelConfig[agent.autonomy_level]?.color || 'bg-gray-100'}>
                      {autonomyLevelConfig[agent.autonomy_level]?.label || agent.autonomy_level}
                    </Badge>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Tasks Completed</p>
                      <p className="font-semibold text-green-600">{agent.tasks_completed || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Tasks Failed</p>
                      <p className="font-semibold text-red-600">{agent.tasks_failed || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Decisions Made</p>
                      <p className="font-semibold">{agent.decisions_made || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Escalations</p>
                      <p className="font-semibold text-amber-600">{agent.escalations || 0}</p>
                    </div>
                  </div>

                  {/* Performance */}
                  {agent.success_rate !== null && agent.success_rate !== undefined && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-500">Success Rate</span>
                        <span className="font-medium">{(agent.success_rate * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${agent.success_rate * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Timing */}
                  <div className="text-xs text-gray-400 space-y-1">
                    {agent.last_run_at && (
                      <p className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last run: {new Date(agent.last_run_at).toLocaleString()}
                      </p>
                    )}
                    {agent.next_run_at && (
                      <p className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Next run: {new Date(agent.next_run_at).toLocaleString()}
                      </p>
                    )}
                    {agent.avg_completion_time_seconds && (
                      <p className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Avg time: {agent.avg_completion_time_seconds.toFixed(2)}s
                      </p>
                    )}
                  </div>

                  {/* Allowed Actions */}
                  {agent.allowed_actions && agent.allowed_actions.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-gray-500 mb-2">Allowed Actions:</p>
                      <div className="flex flex-wrap gap-1">
                        {agent.allowed_actions.slice(0, 3).map((action, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {action}
                          </Badge>
                        ))}
                        {agent.allowed_actions.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{agent.allowed_actions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Available Roles Reference */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Available Agent Roles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {agentMeta.roles.map((role) => (
                <Badge key={role} variant="outline" className="capitalize">
                  {role.replace('_', ' ')}
                </Badge>
              ))}
              {agentMeta.roles.length === 0 && (
                <p className="text-sm text-gray-400">No roles loaded</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AutonomyAgents;
